import { Phone, Mail, MapPin, Clock, } from "lucide-react";
import { LeadflowForm } from "@/components/LeadflowForm";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionReveal from "@/components/SectionReveal";
import WaveDivider from "@/components/WaveDivider";
import ProtectedEmail from "@/components/ProtectedEmail";

const contactInfo = [
  { icon: Phone, label: "(773) 934-2004", href: "tel:17739342004" },
  { icon: Mail, label: "", href: "", email: true },
  { icon: MapPin, label: "2313 Evergreen Ave, Fox River Grove, IL 60021", href: "https://maps.app.goo.gl/Tbt848wzbXLTv6nb7" },
  { icon: Clock, label: "Mon–Fri 8AM–6PM · Sat & Sun Closed" },
];

const Contact = () => {
  return (
    <Layout>
      <SEOHead
        title="Contact & Get a Free Estimate | JTB Floors"
        description="Request a free flooring estimate from JTB Floors in Barrington, IL. Fill out the form or call (773) 934-2004. Serving Barrington and 35 miles out."
      />

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h1 className="text-4xl md:text-5xl font-heading font-black mb-4">Request a Free Estimate</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Fill out the form below or call us directly. We serve Barrington, IL and surrounding areas within 35 miles.
            </p>
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--muted))" bottomColor="hsl(var(--background))" />

      {/* Contact Info Cards */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, i) => (
              <SectionReveal key={i} delay={i * 0.08}>
                <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-5 h-5 text-primary" />
                  </div>
                  {"email" in info ? (
                    <ProtectedEmail className="text-sm font-medium text-foreground hover:text-primary transition-colors break-all" />
                  ) : info.href ? (
                    <a href={info.href} target={info.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm font-medium text-foreground hover:text-primary transition-colors break-all">
                      {info.label}
                    </a>
                  ) : (
                    <span className="text-sm font-medium text-foreground">{info.label}</span>
                  )}
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 lg:py-20" id="form">
        <div className="container mx-auto px-4 lg:px-8 max-w-2xl">
          <SectionReveal>
            <h2 className="text-2xl font-heading font-bold mb-2">Tell Us About Your Flooring Project</h2>
            <LeadflowForm />
          </SectionReveal>
        </div>
      </section>

      <WaveDivider topColor="hsl(var(--background))" bottomColor="hsl(var(--muted))" />

      {/* Map */}
      <section className="py-12 lg:py-16 bg-muted">
        <div className="container mx-auto px-4 lg:px-8">
          <SectionReveal>
            <h2 className="text-2xl font-heading font-bold mb-6 text-center">Find Us</h2>
            <div className="rounded-xl overflow-hidden border border-border shadow-lg mb-4">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2958.5!2d-88.2156!3d42.2003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880f1c2b1b1b1b1b%3A0x1b1b1b1b1b1b1b1b!2s2313+Evergreen+Ave%2C+Fox+River+Grove%2C+IL+60021!5e0!3m2!1sen!2sus!4v1700000000000"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="JTB Floors location — 2313 Evergreen Ave, Fox River Grove, IL 60021"
                className="w-full"
              />
            </div>
            <div className="text-center">
              <a
                href="https://maps.app.goo.gl/Tbt848wzbXLTv6nb7"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
              >
                <MapPin className="w-5 h-5" />
                Open in Google Maps — 2313 Evergreen Ave, Fox River Grove, IL 60021
              </a>
            </div>
          </SectionReveal>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
